<template>
  <div class="attachments-list">
    <!-- Aggregation info banner -->
    <div class="aggregation-info">
      <font-awesome-icon :icon="['fas', 'info-circle']" />
      <span>Showing attachments from this post and all replies</span>
    </div>

    <!-- Loading state -->
    <div v-if="loading && attachments.length === 0" class="loading-state">
      <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
      <p>Loading attachments...</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="attachments.length === 0" class="empty-state">
      <font-awesome-icon :icon="['fas', 'paperclip']" size="2x" />
      <p>No attachments found.</p>
    </div>

    <!-- Attachments list -->
    <div v-else class="attachments-container">
      <div
        v-for="attachment in attachments"
        :key="attachment.id"
        class="attachment-card box"
      >
        <div class="attachment-header">
          <span class="attachment-type-badge" :class="getTypeBadgeClass(attachment.type)">
            {{ attachment.type }}
          </span>
          <span class="attachment-timestamp">{{ formatTimestamp(attachment.created_at) }}</span>
        </div>

        <div class="attachment-file">
          <font-awesome-icon :icon="['fas', 'file']" class="file-icon" />
          <span class="file-name">{{ attachment.original_filename || 'Unknown file' }}</span>
          <button
            class="btn-download"
            @click.stop="downloadAttachment(attachment)"
            title="Download file"
          >
            <font-awesome-icon :icon="['fas', 'download']" />
          </button>
        </div>

        <div v-if="attachment.metadata" class="attachment-metadata">
          <div class="metadata-label">Metadata:</div>
          <pre class="metadata-content">{{ formatMetadata(attachment.metadata) }}</pre>
        </div>
      </div>
    </div>

    <!-- Load more trigger -->
    <div v-if="hasMore" id="load-more-attachments-trigger" class="load-more">
      <div v-if="loadingMore" class="loading-more">
        <font-awesome-icon :icon="['fas', 'spinner']" spin />
        Loading more...
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import Submission from '@/api/Submission.js'
import moment from 'moment'

const props = defineProps({
  submissionId: {
    type: Number,
    required: true
  }
})

// State
const attachments = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(false)
const offset = ref(0)
const limit = 20

// Observer for infinite scroll
let observer = null

// Fetch attachments
async function fetchAttachments(append = false) {
  if (!append) {
    loading.value = true
    offset.value = 0
    attachments.value = []
  } else {
    loadingMore.value = true
  }

  try {
    const response = await Submission.getSubmissionAttachmentsAggregated(
      props.submissionId,
      offset.value,
      limit
    )

    if (response.status === 'success') {
      // Handle nested response structure
      const responseData = response.data || {}
      const attachmentsArray = responseData.attachments || []

      if (append) {
        attachments.value = [...attachments.value, ...attachmentsArray]
      } else {
        attachments.value = attachmentsArray
      }

      hasMore.value = responseData.has_more || false
      offset.value = offset.value + attachmentsArray.length
    }
  } catch (error) {
    console.error('Failed to fetch attachments:', error)
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// Get filename from path
function getFileName(filePath) {
  if (!filePath) return 'Unknown file'
  const parts = filePath.split('/')
  return parts[parts.length - 1] || filePath
}

// Format timestamp
function formatTimestamp(timestamp) {
  return moment(timestamp).fromNow()
}

// Get type badge class based on attachment type
function getTypeBadgeClass(type) {
  const typeMap = {
    'image': 'badge-image',
    'video': 'badge-video',
    'document': 'badge-document',
    'pdf': 'badge-pdf',
    'text': 'badge-text',
    'archive': 'badge-archive',
    'code': 'badge-code',
    'log': 'badge-log',
    'pcap': 'badge-pcap',
    'screenshot': 'badge-screenshot',
    'malware': 'badge-malware',
    'sample': 'badge-sample'
  }

  const normalizedType = type ? type.toLowerCase() : 'other'
  return typeMap[normalizedType] || 'badge-other'
}

// Format metadata for display
function formatMetadata(metadata) {
  if (!metadata) return ''
  if (typeof metadata === 'string') {
    try {
      return JSON.stringify(JSON.parse(metadata), null, 2)
    } catch {
      return metadata
    }
  }
  return JSON.stringify(metadata, null, 2)
}

// Download attachment
async function downloadAttachment(attachment) {
  try {
    const response = await Submission.downloadAttachment(props.submissionId, attachment.id)

    // Create blob URL from response
    const blob = new Blob([response.data], { type: response.headers['content-type'] })
    const url = window.URL.createObjectURL(blob)

    // Create temporary link and trigger download
    const link = document.createElement('a')
    link.href = url
    link.download = attachment.original_filename || 'download'
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to download attachment:', error)
  }
}

// Setup intersection observer for infinite scroll
function setupIntersectionObserver() {
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && hasMore.value && !loadingMore.value) {
      fetchAttachments(true) // true = append mode
    }
  }, { threshold: 0.1 })

  const trigger = document.querySelector('#load-more-attachments-trigger')
  if (trigger) {
    observer.observe(trigger)
  }
}

// Lifecycle hooks
onMounted(async () => {
  await fetchAttachments(false)

  // Setup intersection observer after initial render
  setTimeout(() => {
    setupIntersectionObserver()
  }, 100)
})

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
.attachments-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Aggregation info banner */
.aggregation-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 0.5rem;
  color: #3b82f6;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #9CA3AF;
  gap: 1rem;
}

.loading-state p {
  margin: 0;
  font-size: 0.9rem;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #6B7280;
  gap: 0.75rem;
}

.empty-state svg {
  color: rgba(255, 255, 255, 0.1);
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
  color: #9CA3AF;
}

/* Attachments container */
.attachments-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Attachment card */
.attachment-card {
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0.75rem;
  transition: all 0.2s;
  cursor: pointer;
}

.attachment-card:hover {
  background: rgba(255, 255, 255, 0.04);
  transform: translateY(-1px);
}

.attachment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.attachment-type-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Type badge colors */
.badge-image {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.badge-video {
  background: rgba(168, 85, 247, 0.2);
  color: #a855f7;
}

.badge-document {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}

.badge-pdf {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.badge-text {
  background: rgba(156, 163, 175, 0.2);
  color: #9ca3af;
}

.badge-archive {
  background: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
}

.badge-code {
  background: rgba(20, 184, 166, 0.2);
  color: #14b8a6;
}

.badge-log {
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
}

.badge-pcap {
  background: rgba(99, 102, 241, 0.2);
  color: #6366f1;
}

.badge-screenshot {
  background: rgba(236, 72, 153, 0.2);
  color: #ec4899;
}

.badge-malware {
  background: rgba(220, 38, 38, 0.2);
  color: #dc2626;
}

.badge-sample {
  background: rgba(234, 88, 12, 0.2);
  color: #ea580c;
}

.badge-other {
  background: rgba(107, 114, 128, 0.2);
  color: #6b7280;
}

.attachment-timestamp {
  font-size: 0.8rem;
  color: #6B7280;
}

.attachment-file {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(25, 31, 39, 0.5);
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
}

.file-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #7357D2;
}

.file-name {
  color: #FFFFFF;
  font-size: 0.9rem;
  font-family: 'Monaco', 'Courier New', monospace;
  word-break: break-all;
  flex: 1;
}

.btn-download {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  background: rgba(115, 87, 210, 0.2);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.375rem;
  color: #7357D2;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-download:hover {
  background: rgba(115, 87, 210, 0.3);
  border-color: rgba(115, 87, 210, 0.5);
  transform: translateY(-1px);
}

.btn-download:active {
  transform: translateY(0);
}

.btn-download svg {
  width: 0.875rem;
  height: 0.875rem;
}

.attachment-metadata {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.metadata-label {
  font-size: 0.75rem;
  color: #9CA3AF;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.metadata-content {
  background: rgba(25, 31, 39, 0.8);
  padding: 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  color: #D1D5DB;
  overflow-x: auto;
  margin: 0;
  font-family: 'Monaco', 'Courier New', monospace;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Load more */
.load-more {
  padding: 1.5rem 0;
  text-align: center;
}

.loading-more {
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.loading-more svg {
  width: 1rem;
  height: 1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .attachment-card {
    padding: 0.875rem;
  }

  .attachment-type-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }

  .attachment-timestamp {
    font-size: 0.75rem;
  }

  .file-name {
    font-size: 0.85rem;
  }

  .metadata-content {
    font-size: 0.75rem;
    padding: 0.625rem;
  }

  .loading-state,
  .empty-state {
    padding: 2rem 1rem;
  }
}
</style>
